export interface SessionHeaders {
  [header: string]: string | undefined;
  'x-id-token'?: string;
  'x-userinfo'?: string;
  authorization?: string;
}

export interface SessionEntry {
  sub: string;
  name?: string;
  headers: SessionHeaders;
  capturedAt: number;
}

const CATALOG_KEY = 'devtools:sessions';
// `manual-session` is the contract key the API client reads (see admin/shared/api).
// The applied override lives here so the openapi-fetch clients pick it up.
const ACTIVE_KEY = 'manual-session';

const SESSION_HEADER_KEYS = ['x-id-token', 'x-userinfo', 'authorization'] as const;

export function pickSessionHeaders(headers: Record<string, string | undefined>): SessionHeaders {
  const picked: SessionHeaders = {};

  for (const key of SESSION_HEADER_KEYS) {
    if (headers[key]) {
      picked[key] = headers[key];
    }
  }

  return picked;
}

function readCatalog(): Record<string, SessionEntry> {
  try {
    return JSON.parse(globalThis.localStorage?.getItem(CATALOG_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function readSessions(): SessionEntry[] {
  return Object.values(readCatalog()).sort((a, b) => b.capturedAt - a.capturedAt);
}

const listeners = new Set<() => void>();
let snapshot = readSessions();

function emit() {
  snapshot = readSessions();
  listeners.forEach((notify) => notify());
}

// Another tab capturing or applying a session writes the same keys; mirror it here.
globalThis.addEventListener?.('storage', (event: StorageEvent) => {
  if (event.key === CATALOG_KEY) {
    emit();
  }
});

export function subscribeSessions(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getSessionsSnapshot(): SessionEntry[] {
  return snapshot;
}

/** Upsert a session, deduped by `sub`: the latest capture for a subject wins. */
export function upsertSession(entry: SessionEntry) {
  const catalog = readCatalog();
  catalog[entry.sub] = entry;
  globalThis.localStorage?.setItem(CATALOG_KEY, JSON.stringify(catalog));
  emit();
}

export function removeSession(sub: string) {
  const catalog = readCatalog();
  delete catalog[sub];
  globalThis.localStorage?.setItem(CATALOG_KEY, JSON.stringify(catalog));
  emit();
}

export function getActiveHeaders(): SessionHeaders {
  try {
    return JSON.parse(globalThis.localStorage?.getItem(ACTIVE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

/** Inject a session into the API client and reload so every request picks it up. */
export function applySession(headers: SessionHeaders) {
  globalThis.localStorage?.setItem(ACTIVE_KEY, JSON.stringify(headers));
  globalThis.location?.reload();
}

/** Drop the applied override without reloading (e.g. on logout). */
export function discardActiveSession() {
  globalThis.localStorage?.removeItem(ACTIVE_KEY);
}

export function clearActiveSession() {
  discardActiveSession();
  globalThis.location?.reload();
}

// Handles both standard base64 (`x-userinfo`) and base64url (JWT segments).
function decodeBase64(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

  return atob(padded);
}

export function decodeUserInfo(headers: SessionHeaders): Record<string, unknown> | null {
  const raw = headers['x-userinfo'];

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64(raw));
  } catch {
    return null;
  }
}

export interface JwtClaims {
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

export function decodeJwt(token?: string): JwtClaims | null {
  const payload = token?.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64(payload));
  } catch {
    return null;
  }
}

/** Two sessions match when they share any identity token. */
export function isSameSession(a: SessionHeaders, b: SessionHeaders): boolean {
  return SESSION_HEADER_KEYS.some((key) => Boolean(a[key]) && a[key] === b[key]);
}
